import { z } from "zod";

/**
 * The typed JSON columns of `restaurants`. See docs/architecture/data-model.md.
 * Every field has a default, so a stored `{}` and rows written before a field
 * existed parse to complete values.
 */

/**
 * Storefront accent colours, from the curated list in
 * `@luckymenu/design-tokens` (accents.ts), where each is contrast checked.
 */
export const ACCENT_COLORS = [
	"clover",
	"saffron",
	"chilli",
	"turmeric",
	"teal",
	"ocean",
	"plum",
	"berry",
	"cocoa",
	"charcoal",
] as const;
export const accentColorSchema = z.enum(ACCENT_COLORS);
export type AccentColor = z.infer<typeof accentColorSchema>;

/** `restaurants.settings` */
/**
 * Style presets for generated item photos (ai.md, Feature 2). One per
 * restaurant keeps the menu consistent. The default waits on the owner's
 * taste (phase-08 "Needs the owner"); until then, bright overhead.
 */
export const IMAGE_STYLE_PRESETS = ["bright_overhead", "warm_table", "dark_moody"] as const;
export const imageStylePresetSchema = z.enum(IMAGE_STYLE_PRESETS);
export type ImageStylePreset = z.infer<typeof imageStylePresetSchema>;
export const DEFAULT_IMAGE_STYLE: ImageStylePreset = "bright_overhead";

export const restaurantSettingsSchema = z.object({
	qrOrderingEnabled: z.boolean().default(true),
	autoAcceptQrOrders: z.boolean().default(false),
	serviceRequestsEnabled: z.boolean().default(true),
	/** Takeaway from the public site. Off by default (open question Q15). */
	takeawayEnabled: z.boolean().default(false),
	askGuestName: z.boolean().default(false),
	/** A "Popular" section at the top of the storefront menu, from 30 days of orders (P11-T09). */
	showPopular: z.boolean().default(true),
	/** Menu languages, first is the default. English only at launch. */
	languages: z
		.array(z.string().regex(/^[a-z]{2}$/))
		.min(1)
		.default(["en"]),
	/**
	 * The storefront's accent colour (P2-T15). Set by the superadmin; the site
	 * editor takes it over in Phase 7 (`SiteConfig.theme.accent`).
	 */
	accent: accentColorSchema.default("clover"),
	/** The style of generated item photos (P8-T15), one per restaurant so the menu looks consistent. */
	imageStyle: imageStylePresetSchema.default(DEFAULT_IMAGE_STYLE),
	/** Show "Representative image" under AI-generated photos on the storefront (P8-T20, Q14). */
	showAiImageNote: z.boolean().default(true),
});
export type RestaurantSettings = z.infer<typeof restaurantSettingsSchema>;

export const DEFAULT_RESTAURANT_SETTINGS: RestaurantSettings = restaurantSettingsSchema.parse({});

export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "HH:MM, 24 hour");

/**
 * One opening period. `close` earlier than `open` means the period runs past
 * midnight, as for a restaurant open 18:00 to 01:00.
 */
export const openingPeriodSchema = z
	.object({ open: timeOfDaySchema, close: timeOfDaySchema })
	.refine((p) => p.open !== p.close, { message: "Open and close must differ" });

export type OpeningPeriod = z.infer<typeof openingPeriodSchema>;

/** `restaurants.hours`: periods per weekday. A missing or empty day is closed. */
export const openingHoursSchema = z.partialRecord(
	z.enum(WEEKDAYS),
	z.array(openingPeriodSchema).max(4),
);
export type OpeningHours = z.infer<typeof openingHoursSchema>;

/** Indian PIN code: six digits, not starting with 0. */
export const pincodeSchema = z.string().regex(/^[1-9]\d{5}$/, "Six digit PIN code");

/** GST state code, two digits, as in the first two characters of a GSTIN. "18" is Assam. */
export const stateCodeSchema = z.string().regex(/^\d{2}$/, "Two digit GST state code");

/**
 * A GSTIN as printed: state code, PAN, entity number, "Z", check character.
 * Typed input is trimmed and upper-cased. The check character is not verified
 * here: `checkedGstinSchema` does that, for restaurants' and diners' GSTINs.
 */
export const gstinSchema = z
	.string()
	.trim()
	.toUpperCase()
	.regex(/^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/, "A 15 character GSTIN");

const GSTIN_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";

/**
 * The 15th character of a GSTIN, from the first 14: each character's value
 * (0-9, then A-Z as 10-35) is multiplied by 1 and 2 alternately, each
 * product's quotient and remainder by 36 are added up, and the check is
 * (36 − sum mod 36) mod 36.
 */
export function gstinCheckCharacter(first14: string): string {
	let sum = 0;
	for (let i = 0; i < 14; i++) {
		const value = GSTIN_CHARS.indexOf(first14[i] ?? "");
		if (value < 0) throw new RangeError(`Not a GSTIN character: ${first14[i]}`);
		const product = value * (i % 2 === 0 ? 1 : 2);
		sum += Math.floor(product / 36) + (product % 36);
	}
	return GSTIN_CHARS[(36 - (sum % 36)) % 36] as string;
}

/** A GSTIN in the right format whose check character matches (P4-T12). */
export const checkedGstinSchema = gstinSchema.refine(
	(gstin) => gstinCheckCharacter(gstin.slice(0, 14)) === gstin[14],
	"This GSTIN has a typing mistake: its last character does not match",
);
